import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BandChart from '../components/plants/BandChart';
import FamilyModal from '../components/plants/FamilyModal';
import DevicePreviewModal from '../components/plants/DevicePreviewModal';
import { getPlantProfile, updatePlantProfile, updatePlantFamily, getPlantProfileOverlays } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { C, METRICS, SWATCH, METRIC_LABELS, PHENOLOGY_OPTIONS, MONTHS, PREVIEWABLE_METRICS, defaultParameters } from '../utils/plantConstants';
import { lvlAsc, baseLabel, classPeaksNumeric, localizeParams } from '../utils/plantModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faRotateLeft, faFloppyDisk, faPen, faChartLine } from '@fortawesome/free-solid-svg-icons';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Normalize parameters coming from the API (fill any missing pieces).
function normalize(params) {
	const d = defaultParameters();
	if (!params) return d;
	return {
		phenology_class: params.phenology_class || d.phenology_class,
		hemisphere: params.hemisphere === 'south' ? 'south' : 'north',
		base_levels: { ...d.base_levels, ...(params.base_levels || {}) },
		peaks: Array.isArray(params.peaks) ? params.peaks.map((p) => [p[0], p[1], p[2]]) : d.peaks,
		band_scale: { opt: params.band_scale?.opt ?? 1, tol: params.band_scale?.tol ?? 1 },
	};
}

const Card = ({ label, action, children }) => (
	<div className="bg-white border border-gray-200 rounded-xl p-4">
		<div className="flex items-center justify-between mb-3">
			<span className="text-[11px] font-semibold tracking-wide uppercase text-text-weak">{label}</span>
			{action}
		</div>
		{children}
	</div>
);

const PlantProfileEditorPage = () => {
	const { profileId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { showToast } = useToast();

	// Go back to wherever the user came from (device page or Plants list);
	// fall back to the list on a direct load with no in-app history.
	const goBack = () => {
		if (location.key && location.key !== 'default') navigate(-1);
		else navigate('/plants');
	};

	const [profile, setProfile] = useState(null);
	const [params, setParams] = useState(defaultParameters());
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [dirty, setDirty] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [savingFamily, setSavingFamily] = useState(false);
	// Which metric's device preview is open, or null for none. Only the metrics
	// whose health check reads a profile band can be previewed.
	const [previewMetric, setPreviewMetric] = useState(null);
	const [overlays, setOverlays] = useState([]); // [{ country, parameters }]
	const [previewCountry, setPreviewCountry] = useState(''); // '' = no overlay (ideal)

	useEffect(() => {
		getPlantProfileOverlays()
			.then((r) => (r.ok ? r.json() : { data: [] }))
			.then((j) => setOverlays(j.data || []))
			.catch(() => setOverlays([]));
	}, []);

	useEffect(() => {
		const fetchProfile = async () => {
			try {
				const response = await getPlantProfile(profileId);
				if (!response.ok) throw new Error('Failed to fetch plant profile');
				const data = await response.json();
				setProfile(data.data);
				setParams(normalize(data.data.parameters));
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};
		fetchProfile();
	}, [profileId]);

	const update = useCallback((updater) => {
		setParams((prev) => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
		setDirty(true);
	}, []);

	const setClass = (cls) => update((p) => ({ ...p, phenology_class: cls, peaks: classPeaksNumeric(cls) }));
	const setHemisphere = (h) => update((p) => ({ ...p, hemisphere: h }));
	const setBase = (metric, frac) => update((p) => ({ ...p, base_levels: { ...p.base_levels, [metric]: frac } }));
	const setPeak = (i, field, val) =>
		update((p) => {
			const peaks = p.peaks.map((pk) => [...pk]);
			peaks[i][field] = val;
			return { ...p, peaks };
		});
	const resetShape = () => update((p) => ({ ...p, peaks: classPeaksNumeric(p.phenology_class) }));
	const setScale = (key, val) => update((p) => ({ ...p, band_scale: { ...p.band_scale, [key]: val } }));
	const resetBands = () => update((p) => ({ ...p, band_scale: { opt: 1, tol: 1 } }));

	const handleSave = async () => {
		try {
			setSaving(true);
			const payload = {
				description: profile?.description || '',
				phenology_class: params.phenology_class,
				hemisphere: params.hemisphere,
				base_levels: params.base_levels,
				peaks: params.peaks,
				band_scale: params.band_scale,
			};
			const response = await updatePlantProfile(profileId, payload);
			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new Error(data.message || data.error?.message || 'Failed to save profile');
			}
			const data = await response.json();
			setProfile(data.data);
			setDirty(false);
			showToast('success', 'Profile saved');
			navigate('/plants');
		} catch (err) {
			showToast('error', err.message);
		} finally {
			setSaving(false);
		}
	};

	const handleEditFamily = async (familyName, description) => {
		try {
			setSavingFamily(true);
			const response = await updatePlantFamily(profileId, { family: familyName, description });
			const data = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(data.message || data.error?.message || 'Failed to update family');
			}
			setProfile(data.data);
			setIsEditOpen(false);
			showToast('success', 'Family updated');
		} catch (err) {
			showToast('error', err.message);
		} finally {
			setSavingFamily(false);
		}
	};

	if (loading) {
		return (
			<div className="flex flex-col min-h-screen">
				<Header />
				<main className="flex-grow container mx-auto px-4 py-8"><p>Loading profile...</p></main>
				<Footer />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col min-h-screen">
				<Header />
				<main className="flex-grow container mx-auto px-4 py-8"><p className="text-red-500">Error: {error}</p></main>
				<Footer />
			</div>
		);
	}

	// The general profile is the reserved fallback: its name is what the device
	// pages call an unidentified plant, so it is not renameable — but its
	// parameters are exactly what this editor is for.
	const isGeneral = !!profile?.is_general;
	const subtitle = `${C.classes[params.phenology_class].label} · ${params.hemisphere}ern hemisphere`;

	// Apply the selected location overlay as a preview (the saved profile stays
	// canonical — this only changes what the charts show).
	const selectedOverlay = overlays.find((o) => o.country === previewCountry);
	const viewParams = selectedOverlay ? localizeParams(params, selectedOverlay.parameters) : params;

	return (
		<div className="flex flex-col min-h-screen bg-gray-50">
			<Header />
			<main className="flex-grow container mx-auto px-4 py-8">
				<div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
					<div className="flex items-center gap-3">
						<button onClick={goBack} className="w-9 h-9 bg-white border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50" title="Back">
							<FontAwesomeIcon icon={faArrowLeft} className="text-text-weak" />
						</button>
						<div>
							<div className="flex items-center gap-2">
								<h1 data-testid="editor-family" className="text-2xl font-bold">{profile?.family}</h1>
								{isGeneral ? (
									<span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-600">
										Fallback
									</span>
								) : (
									<button
										onClick={() => setIsEditOpen(true)}
										data-testid="editor-edit-family"
										className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-text-strong transition-colors"
										title="Edit family name"
									>
										<FontAwesomeIcon icon={faPen} className="text-sm" />
									</button>
								)}
							</div>
							<p className="text-sm text-text-weak">{subtitle}</p>
							{isGeneral && (
								<p className="text-xs text-text-weak mt-0.5">
									Every device with no identified plant family — and every family nobody has configured — is graded against this profile.
								</p>
							)}
						</div>
					</div>
					<div className="flex items-center gap-2">
						{(() => {
							const canSave = dirty || !profile?.configured;
							return (
								<button
									onClick={handleSave}
									data-testid="editor-save"
									disabled={saving || !canSave}
									className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
								>
									<FontAwesomeIcon icon={faFloppyDisk} />
									{saving ? 'Saving...' : canSave ? 'Save profile' : 'Saved'}
								</button>
							);
						})()}
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 items-start">
					{/* Left: controls */}
					<div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
						{/* Phenology & hemisphere */}
						<Card label="Phenology & hemisphere">
							<select
								data-testid="editor-phenology"
								value={params.phenology_class}
								onChange={(e) => setClass(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
							>
								{PHENOLOGY_OPTIONS.map((o) => (
									<option key={o.value} value={o.value}>{o.label}</option>
								))}
							</select>
							<div className="flex gap-1 bg-gray-100 border border-gray-200 rounded-lg p-1">
								{['north', 'south'].map((h) => (
									<button
										key={h}
										onClick={() => setHemisphere(h)}
										data-testid="editor-hemisphere"
										data-hemisphere={h}
										data-selected={params.hemisphere === h ? 'true' : 'false'}
										className={`flex-1 py-1.5 text-sm rounded-md transition-colors ${params.hemisphere === h ? 'bg-white shadow-sm font-medium' : 'text-text-weak'}`}
									>
										{h === 'north' ? 'Northern' : 'Southern'}
									</button>
								))}
							</div>
						</Card>

						{/* Base levels */}
						<Card label="Base level">
							<div className="flex flex-col gap-4">
								{METRICS.map((metric) => {
									const asc = lvlAsc(metric);
									return (
										<div key={metric}>
											<div className="flex items-center justify-between mb-1">
												<span className="flex items-center gap-2 text-sm text-text-strong">
													<span className="w-2.5 h-2.5 rounded-sm" style={{ background: SWATCH[metric] }} />
													{METRIC_LABELS[metric]}
												</span>
												<span className="text-xs font-mono capitalize" style={{ color: SWATCH[metric] }}>
													{baseLabel(metric, params.base_levels[metric])}
												</span>
											</div>
											<input
												type="range"
												min={0}
												max={asc.length - 1}
												step={0.01}
												value={params.base_levels[metric]}
												data-testid="editor-base-slider"
												data-metric={metric}
												onChange={(e) => setBase(metric, parseFloat(e.target.value))}
												className="w-full"
												style={{ accentColor: SWATCH[metric] }}
											/>
											<div className="flex justify-between text-[9.5px] text-gray-400 font-mono mt-0.5">
												<span>{cap(asc[0])}</span>
												<span>{cap(asc[asc.length - 1])}</span>
											</div>
										</div>
									);
								})}
							</div>
						</Card>

						{/* Seasonal shape */}
						<Card
							label="Seasonal shape"
							action={
								params.peaks.length > 0 && (
									<button onClick={resetShape} data-testid="editor-reset-shape" className="text-xs text-text-weak hover:text-text-strong flex items-center gap-1">
										<FontAwesomeIcon icon={faRotateLeft} /> Reset
									</button>
								)
							}
						>
							{params.peaks.length === 0 ? (
								<p className="text-xs text-text-weak">This class is flat — no seasonal peaks. Pick another class to shape a growth season.</p>
							) : (
								<div className="flex flex-col gap-4">
									{params.peaks.map((peak, i) => (
										<div key={i} className="border border-gray-100 rounded-lg p-3">
											<div className="text-xs font-semibold text-text-strong mb-2">Growth peak {i + 1}</div>
											<SliderRow label="Center" value={MONTHS[Math.round(peak[0]) - 1] || peak[0]} min={1} max={12} step={1} val={peak[0]} onChange={(v) => setPeak(i, 0, v)} />
											<SliderRow label="Height" value={peak[1].toFixed(2)} min={0} max={1} step={0.01} val={peak[1]} onChange={(v) => setPeak(i, 1, v)} />
											<SliderRow label="Width" value={peak[2].toFixed(1)} min={0.5} max={4} step={0.1} val={peak[2]} onChange={(v) => setPeak(i, 2, v)} />
										</div>
									))}
									<div className="text-xs text-text-weak font-mono">Temp amplitude: {C.classes[params.phenology_class].temp_amp} °C</div>
								</div>
							)}
						</Card>

						{/* Band tolerances */}
						<Card
							label="Band tolerances"
							action={
								<button onClick={resetBands} data-testid="editor-reset-bands" className="text-xs text-text-weak hover:text-text-strong flex items-center gap-1">
									<FontAwesomeIcon icon={faRotateLeft} /> Reset
								</button>
							}
						>
							<SliderRow label="Optimal band" value={`${Math.round(params.band_scale.opt * 100)}%`} min={0.4} max={1.6} step={0.05} val={params.band_scale.opt} onChange={(v) => setScale('opt', v)} />
							<SliderRow label="Tolerated margin" value={`${Math.round(params.band_scale.tol * 100)}%`} min={0.4} max={1.6} step={0.05} val={params.band_scale.tol} onChange={(v) => setScale('tol', v)} />
						</Card>
					</div>

					{/* Right: charts */}
					<div>
						<div className="mb-3 flex items-end justify-between gap-4 flex-wrap">
							<div>
								<h2 data-testid="editor-chart-heading" className="text-lg font-semibold">{selectedOverlay ? `Profile in ${selectedOverlay.country}` : 'Yearly ideal profile'}</h2>
								<p className="text-sm text-text-weak">Target and optimal bands across the year for each metric.</p>
							</div>
							<div>
								<label className="block text-[11px] font-semibold tracking-wide uppercase text-text-weak mb-1">Location preview</label>
								<select
									data-testid="editor-location-preview"
									value={previewCountry}
									onChange={(e) => setPreviewCountry(e.target.value)}
									className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
								>
									<option value="">None (ideal)</option>
									{overlays.map((o) => (
										<option key={o.country} value={o.country}>{o.country}</option>
									))}
								</select>
							</div>
						</div>
						<div className="flex flex-col gap-3.5">
							{METRICS.map((metric) => (
								<BandChart
									key={metric}
									metric={metric}
									params={viewParams}
									action={
										PREVIEWABLE_METRICS.includes(metric) && (
											<button
												onClick={() => setPreviewMetric(metric)}
												data-testid="editor-preview-devices"
												data-metric={metric}
												disabled={!profile?.configured}
												title={
													profile?.configured
														? isGeneral
															? `See how this ${METRIC_LABELS[metric].toLowerCase()} band grades the devices that fall back to this profile`
															: `See how this ${METRIC_LABELS[metric].toLowerCase()} band grades real devices`
														: 'Save the profile once before previewing it on devices'
												}
												className="ml-3 bg-white border border-gray-200 text-text-strong text-xs px-2.5 py-1 rounded-md flex items-center gap-1.5 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
											>
												<FontAwesomeIcon icon={faChartLine} />
												Preview on devices
											</button>
										)
									}
								/>
							))}
						</div>
					</div>
				</div>
			</main>
			<Footer />

			<FamilyModal
				isOpen={isEditOpen && !isGeneral}
				mode="edit"
				initialFamily={profile?.family || ''}
				initialDescription={profile?.description || ''}
				submitting={savingFamily}
				onClose={() => setIsEditOpen(false)}
				onSubmit={handleEditFamily}
			/>

			{/* params, not viewParams: each device is graded against its own
			    site's overlay, not the Location preview selection. */}
			<DevicePreviewModal
				isOpen={!!previewMetric}
				onClose={() => setPreviewMetric(null)}
				profileId={profileId}
				metric={previewMetric}
				params={params}
				isGeneral={isGeneral}
			/>
		</div>
	);
};

// A labeled range row used by the seasonal-shape and band-tolerance cards.
const SliderRow = ({ label, value, min, max, step, val, onChange }) => (
	<div className="mb-2 last:mb-0">
		<div className="flex items-center justify-between mb-0.5">
			<span className="text-xs text-text-weak">{label}</span>
			<span className="text-xs font-mono text-text-strong">{value}</span>
		</div>
		<input type="range" min={min} max={max} step={step} value={val} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full" />
	</div>
);

export default PlantProfileEditorPage;
